import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { Body, Button, Eyebrow, I, Row, Stack } from '@leanin/ui';

import { GroupLogoBlock } from './GroupLogoBlock';
import {
  useCommitGroupImage,
  useRequestGroupImageUpload,
  type GroupImageContentType,
  type GroupImageField,
} from '../../lib/mutations/groups';
import { errorMessage } from '../../lib/error-message';
import { uploadWithProgress } from '../../lib/upload';
import { ImageCropDialog, type ImageCropRect } from '../media/ImageCropDialog';
import { fetchImageAsFile } from '../media/fetch-image-file';

const ACCEPTED_TYPES: ReadonlyArray<GroupImageContentType> = [
  'image/jpeg',
  'image/png',
  'image/webp',
];
const MAX_BYTES = 10 * 1024 * 1024;
// Cover renders 4:1 (banner surfaces); logo renders square in GroupLogoBlock.
const CROP_ASPECTS: Record<GroupImageField, number> = { cover: 4, logo: 1 };

// Shared upload affordance for circle + network + space leaders. Handles
// both the brand-block logo and the wide cover image.
//
// Deferred-commit shape: picking a file opens a crop dialog, and
// confirming the crop only *stages* the upload — it
// renders a local blob-URL preview and dirties the field, but the
// presign → PUT → server commit does NOT run yet. The parent form's
// "Save changes" button stays in charge: when the user clicks it, the
// parent calls `commit()` on this field's ref, which then runs the
// upload pipeline and clears the pending state.
//
// Originals retention: the picked file is the *original*; the crop dialog
// produces the *render* plus a source-pixel crop rect. commit() uploads
// both objects (two presigns — each returns a fresh key) and sends
// `{ key, originalKey, crop }` so the server keeps the original for
// later re-crops. The Adjust button re-crops from the true original when
// one exists (in-memory or via `currentOriginalUrl`), restoring the saved
// rect; legacy rows without a stored original fall back to re-cropping
// the displayed render, which then becomes the new original.
//
// Spaces only support `cover` (no logo) — they're topics, not brands.
export type GroupImageUploadFieldProps =
  | {
      readonly groupKind: 'circle' | 'network';
      readonly groupId: string;
      readonly groupName: string;
      readonly field: GroupImageField;
      readonly currentImageUrl: string | null;
      /** Committed pre-crop original for this field (`imageOriginalUrl` /
       *  `logoOriginalUrl` from `GroupDetail`). Lets Adjust re-crop from
       *  the true original instead of the rendered crop. Null/omitted on
       *  legacy rows and create surfaces. */
      readonly currentOriginalUrl?: string | null;
      /** Stored crop rect (source pixels) that produced the committed
       *  render — restores the dialog framing on Adjust. */
      readonly currentCrop?: ImageCropRect | null;
      /** Notifies the parent when the field's dirty state flips, so it
       *  can include the image in its own dirty calc. */
      readonly onPendingChange?: (hasPending: boolean) => void;
      /** Override for the eyebrow label. Defaults to the field-typed
       *  label from `LABELS` ("Logo" / "Cover"). Pass when the caller
       *  wants a more specific phrasing (e.g. "Circle cover image"). */
      readonly eyebrowOverride?: string;
    }
  | {
      readonly groupKind: 'space';
      readonly id: string;
      readonly groupName: string;
      readonly field: GroupImageField;
      readonly currentImageUrl: string | null;
      readonly currentOriginalUrl?: string | null;
      readonly currentCrop?: ImageCropRect | null;
      readonly onPendingChange?: (hasPending: boolean) => void;
      readonly eyebrowOverride?: string;
    };

export type GroupImageUploadFieldHandle = {
  readonly hasPending: boolean;
  /** Runs presign → PUT → server commit for the staged file, then
   *  clears the pending state. No-op when nothing is staged. Rejects
   *  if the upload or commit fails — the caller's save handler should
   *  await this before its own update, and treat a rejection as a
   *  Save failure.
   *
   *  `identifierOverride` is for callers (e.g. /circles/new) where the
   *  group id only exists *after* the parent's create mutation
   *  resolves. The parent can pass the freshly-returned id without
   *  having to wait for a React re-render of this field's `groupId`
   *  prop. When omitted, falls back to the prop-derived identifier. */
  readonly commit: (identifierOverride?: string) => Promise<void>;
  /** Discards the staged file (revokes its blob URL). Used by the
   *  parent's Cancel path. */
  readonly reset: () => void;
};

export const GroupImageUploadField = forwardRef<
  GroupImageUploadFieldHandle,
  GroupImageUploadFieldProps
>(function GroupImageUploadField(props, ref) {
  const { groupKind, groupName, field, currentImageUrl, onPendingChange } = props;
  const currentOriginalUrl = props.currentOriginalUrl ?? null;
  const currentCrop = props.currentCrop ?? null;
  const identifier = props.groupKind === 'space' ? props.id : props.groupId;
  const t = useTranslations('Group');
  const tCommon = useTranslations('Common');
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progressPct, setProgressPct] = useState<number | null>(null);
  // Server-committed URL: set after a successful commit() so the local
  // preview survives parent refetches landing momentarily later.
  const [committedUrl, setCommittedUrl] = useState<string | null>(null);
  // Committed original + crop from the commit response — same
  // refetch-lag shield as `committedUrl`, but for the Adjust source. The
  // `currentOriginalUrl`/`currentCrop` props stay stale until the parent
  // refetches; this takes priority in the meantime.
  const [committedOriginal, setCommittedOriginal] = useState<{
    readonly url: string;
    readonly crop: ImageCropRect | null;
  } | null>(null);
  const [cropSource, setCropSource] = useState<{
    readonly file: File;
    readonly initialCrop: ImageCropRect | null;
  } | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingFileType, setPendingFileType] = useState<GroupImageContentType | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null);
  // Pre-crop source of the staged render, uploaded alongside it on
  // commit so the server retains it for later re-crops.
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [cropRect, setCropRect] = useState<ImageCropRect | null>(null);
  // True for the whole commit() pipeline — covers the original's PUT,
  // which reports no progress (the render PUT owns the progress bar) and
  // would otherwise briefly re-enable the buttons mid-commit.
  const [committing, setCommitting] = useState(false);

  const requestMutation = useRequestGroupImageUpload(groupKind, field);
  const commitMutation = useCommitGroupImage(groupKind, field);
  const isUploading =
    committing ||
    requestMutation.isPending ||
    commitMutation.isPending ||
    progressPct !== null;
  // Display priority: staged blob (pending) > newly-committed URL > the
  // server's existing image > nothing.
  const displayedUrl = pendingPreviewUrl ?? committedUrl ?? currentImageUrl;

  // Revoke the blob URL whenever it changes or the component unmounts.
  // Holding on to revoked blob handles leaks memory on long-lived pages.
  useEffect(() => {
    return () => {
      if (pendingPreviewUrl !== null) URL.revokeObjectURL(pendingPreviewUrl);
    };
  }, [pendingPreviewUrl]);

  // Validation runs on the picked original — the file that commit()
  // actually uploads alongside the cropped render. Passing files then go
  // through the crop dialog before staging.
  const onFilePicked = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type as GroupImageContentType)) {
      setError(t('imageUpload.badType', { field }));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('imageUpload.tooBig', { field }));
      return;
    }
    setError(null);
    setCropSource({ file, initialCrop: null });
  };

  const stage = (render: File, original: File, rect: ImageCropRect) => {
    setError(null);
    // Replace any earlier staged file's blob URL.
    if (pendingPreviewUrl !== null) URL.revokeObjectURL(pendingPreviewUrl);
    const nextUrl = URL.createObjectURL(render);
    setPendingFile(render);
    setPendingFileType(render.type as GroupImageContentType);
    setPendingPreviewUrl(nextUrl);
    setOriginalFile(original);
    setCropRect(rect);
    onPendingChange?.(true);
  };

  const reset = () => {
    if (pendingPreviewUrl !== null) URL.revokeObjectURL(pendingPreviewUrl);
    setCropSource(null);
    setPendingFile(null);
    setPendingFileType(null);
    setPendingPreviewUrl(null);
    setOriginalFile(null);
    setCropRect(null);
    setError(null);
    onPendingChange?.(false);
  };

  const commit = async (identifierOverride?: string): Promise<void> => {
    if (
      pendingFile === null ||
      pendingFileType === null ||
      originalFile === null ||
      cropRect === null
    ) {
      return;
    }
    const effectiveIdentifier = identifierOverride ?? identifier;
    setError(null);
    setCommitting(true);
    try {
      // Two objects, two presigns — each presign call mints a fresh key.
      // The original goes up first, silently (no progress bar); the
      // render PUT below owns the visible progress.
      const originalPresign = await requestMutation.mutateAsync({
        identifier: effectiveIdentifier,
        contentType: originalFile.type as GroupImageContentType,
      });
      await uploadWithProgress({
        url: originalPresign.uploadUrl,
        body: originalFile,
        headers: originalPresign.fields,
        onProgress: () => {},
      });
      const presign = await requestMutation.mutateAsync({
        identifier: effectiveIdentifier,
        contentType: pendingFileType,
      });
      setProgressPct(0);
      await uploadWithProgress({
        url: presign.uploadUrl,
        body: pendingFile,
        headers: presign.fields,
        onProgress: (pct) => setProgressPct(pct),
      });
      const updated = await commitMutation.mutateAsync({
        identifier: effectiveIdentifier,
        key: presign.key,
        originalKey: originalPresign.key,
        crop: cropRect,
      });
      const nextUrl =
        field === 'logo' && 'logoUrl' in updated
          ? updated.logoUrl
          : updated.imageUrl;
      setCommittedUrl(nextUrl ?? null);
      const nextOriginalUrl =
        field === 'logo' ? updated.logoOriginalUrl : updated.imageOriginalUrl;
      const nextCrop = field === 'logo' ? updated.logoCrop : updated.imageCrop;
      setCommittedOriginal(
        nextOriginalUrl !== null ? { url: nextOriginalUrl, crop: nextCrop } : null,
      );
      // Tear down the staged blob — the committed URL takes over.
      if (pendingPreviewUrl !== null) URL.revokeObjectURL(pendingPreviewUrl);
      setPendingFile(null);
      setPendingFileType(null);
      setPendingPreviewUrl(null);
      setOriginalFile(null);
      setCropRect(null);
      onPendingChange?.(false);
      void qc.invalidateQueries();
    } catch (err) {
      const message = errorMessage(err, t('imageUpload.uploadFailed', { field }));
      setError(message);
      // Re-throw so the parent's Save handler sees the failure and can
      // abort its own update / show its own error state.
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setProgressPct(null);
      setCommitting(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      hasPending: pendingFile !== null,
      commit,
      reset,
    }),
    // Disabling because `commit` / `reset` close over many states that
    // already drive re-renders; the imperative handle is read at call
    // time by the parent. Keeping the deps array minimal avoids stale
    // captures while not over-invalidating.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingFile, pendingFileType, pendingPreviewUrl, originalFile, cropRect],
  );

  const pick = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  // Re-crop from the true original wherever one exists — the staged
  // in-memory file, or the committed original fetched back from the
  // store — restoring the saved rect so the dialog reopens at the
  // current framing. Legacy rows without a stored original fall back to
  // re-cropping the displayed render (no initial rect: its pixel space
  // is the render's, not the original's); on confirm that render becomes
  // the new original.
  const [adjustLoading, setAdjustLoading] = useState(false);
  const onAdjust = async () => {
    setError(null);
    if (originalFile !== null) {
      setCropSource({ file: originalFile, initialCrop: cropRect });
      return;
    }
    const knownOriginal =
      committedOriginal ??
      (currentOriginalUrl !== null
        ? { url: currentOriginalUrl, crop: currentCrop }
        : null);
    const sourceUrl = knownOriginal?.url ?? displayedUrl;
    if (sourceUrl === null) return;
    setAdjustLoading(true);
    try {
      setCropSource({
        file: await fetchImageAsFile(sourceUrl, `${field}-image`),
        initialCrop: knownOriginal !== null ? knownOriginal.crop : null,
      });
    } catch {
      setError(tCommon('cropDialog.loadError'));
    } finally {
      setAdjustLoading(false);
    }
  };

  const preview = field === 'logo'
    ? (
      <GroupLogoBlock
        logoUrl={displayedUrl}
        groupId={identifier}
        name={groupName}
        size={72}
      />
    )
    : displayedUrl !== null ? (
      <div
        style={{
          width: '100%',
          height: 220,
          borderRadius: 'var(--radius-md)',
          background: `url(${displayedUrl}) center/cover no-repeat`,
          border: '1px solid var(--border)',
        }}
        role="img"
        aria-label={t('imageUpload.coverAria', { name: groupName })}
      />
    ) : (
      <Stack
        gap="none"
        align="center"
        justify="center"
        style={{
          height: 220,
          borderRadius: 'var(--radius-md)',
          background: 'var(--muted)',
          border: '1px solid var(--border)',
        }}
      >
        <Body size="sm" color="mute">{t('imageUpload.noCover')}</Body>
      </Stack>
    );

  const statusLabel = isUploading
    ? progressPct !== null
      ? t('imageUpload.uploading', { pct: progressPct })
      : commitMutation.isPending
        ? t('imageUpload.finalising')
        : t('imageUpload.preparing')
    : pendingFile !== null
      ? t('imageUpload.pending')
      : null;

  return (
    <Stack gap="md">
      <Row gap="md" align="center" justify="space-between" wrap>
        <Eyebrow>{props.eyebrowOverride ?? t('imageUpload.fieldLabel', { field })}</Eyebrow>
        <Row gap="sm" align="center">
          {displayedUrl !== null ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void onAdjust()}
              disabled={isUploading || adjustLoading}
            >
              {tCommon('cropDialog.adjust')}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            icon={I.upload}
            onClick={pick}
            disabled={isUploading}
          >
            {displayedUrl !== null ? t('imageUpload.replace') : t('imageUpload.upload')}
          </Button>
        </Row>
      </Row>
      {field === 'logo' ? (
        <Row gap="md" align="center">
          {preview}
          {statusLabel !== null ? (
            <Body size="sm" color="mute">{statusLabel}</Body>
          ) : null}
          {error !== null ? (
            <Body size="sm" color="destructive">
              {error}
            </Body>
          ) : null}
        </Row>
      ) : (
        <Stack gap="sm">
          {preview}
          {statusLabel !== null ? (
            <Body size="sm" color="mute">{statusLabel}</Body>
          ) : null}
          {error !== null ? (
            <Body size="sm" color="destructive">
              {error}
            </Body>
          ) : null}
        </Stack>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFilePicked(file);
        }}
      />
      {cropSource !== null ? (
        <ImageCropDialog
          file={cropSource.file}
          initialCrop={cropSource.initialCrop ?? undefined}
          aspect={CROP_ASPECTS[field]}
          onConfirm={(cropped, rect) => {
            const original = cropSource.file;
            setCropSource(null);
            stage(cropped, original, rect);
          }}
          onCancel={() => setCropSource(null)}
        />
      ) : null}
    </Stack>
  );
});
